import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model";
import { Fragment, Slice } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import { Button, cn } from "@workith/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { prosemirrorJSONToYXmlFragment } from "y-prosemirror";
import * as Y from "yjs";
import { createCollaborativeExtensions } from "./CollaborativeEditor";
import { useCompositionDeferredEditable } from "./collaborationEditorState";
import type { CollaborationProvider } from "./collaborationProvider";
import {
  classifySectionSize,
  evaluateInsertionGuard,
  measureSectionEncodedBytes,
  reportOversizedSection,
  type SectionSizeLevel,
} from "./sectionSizeGuard";

const SIZE_MEASURE_DEBOUNCE_MS = 500;
/** Top-level blocks whose text can be re-flowed into several paragraphs. */
const SPLITTABLE_BLOCK_TYPES = new Set(["paragraph", "heading"]);

export interface SectionEditorUser {
  id: string;
  name: string;
  color: string;
}

interface SectionEditorProps {
  documentId: string;
  sectionId: string;
  /** Zero-based manifest position, used for the accessible section boundary. */
  index: number;
  sectionCount: number;
  document: Y.Doc;
  provider: CollaborationProvider;
  hasSynced: boolean;
  canEdit: boolean;
  /**
   * This section's own room ended - it no longer belongs to the document, or its
   * lease did not survive. Scoped to this section: the siblings keep editing.
   */
  isUnavailable?: boolean;
  isActive: boolean;
  /** True only for the section that is the document head, i.e. the first one. */
  allowFrontMatter: boolean;
  user: SectionEditorUser;
  onActivate: (sectionId: string) => void;
  onEditorChange: (sectionId: string, editor: Editor | null) => void;
  onDelete: (sectionId: string) => void;
  onSplit: (sectionId: string) => void;
  measureSectionBytes?: (document: Y.Doc) => number;
  className?: string;
}

/**
 * One section room rendered as its own Tiptap editor.
 *
 * The editor binds to the `default` fragment of the very subdocument the root
 * manifest holds, so a section's history, size and sync state stay independent
 * from its siblings.
 */
export default function SectionEditor({
  documentId,
  sectionId,
  index,
  sectionCount,
  document: sectionDocument,
  provider,
  hasSynced,
  canEdit,
  isUnavailable = false,
  isActive,
  allowFrontMatter,
  user,
  onActivate,
  onEditorChange,
  onDelete,
  onSplit,
  measureSectionBytes = measureSectionEncodedBytes,
  className,
}: SectionEditorProps) {
  const position = index + 1;
  const boundaryLabel = `섹션 ${position} / ${sectionCount}`;
  const isLastRemainingSection = sectionCount <= 1;
  const [sizeLevel, setSizeLevel] = useState<SectionSizeLevel>("ok");
  const [insertionRejected, setInsertionRejected] = useState(false);
  const reportedOversize = useRef(false);
  const allowFrontMatterRef = useRef(allowFrontMatter);
  allowFrontMatterRef.current = allowFrontMatter;

  const extensions = useMemo(
    () =>
      createCollaborativeExtensions({
        document: sectionDocument,
        provider,
        user: { name: user.name, color: user.color },
        allowFrontMatter: () => allowFrontMatterRef.current,
      }),
    [sectionDocument, provider, user.name, user.color]
  );

  const editorClass = useMemo(
    () =>
      cn(
        "prose prose-neutral dark:prose-invert prose-sm sm:prose lg:prose-lg mx-auto focus:outline-none max-w-none",
        "min-h-[160px] p-6",
        className
      ),
    [className]
  );

  // Sticky, like the document-level first sync: a room that synced once keeps
  // its editor open, so a reconnect never takes editing away mid-session.
  const [hasEverSynced, setHasEverSynced] = useState(hasSynced);
  useEffect(() => {
    if (hasSynced) setHasEverSynced(true);
  }, [hasSynced]);

  const { isEditable, onCompositionStart, onCompositionEnd } = useCompositionDeferredEditable(
    canEdit && hasEverSynced
  );

  const editor = useEditor(
    {
      extensions,
      editorProps: {
        attributes: { class: editorClass },
        handlePaste: (view, event, slice) =>
          guardPastedSlice({
            view,
            event,
            slice,
            onRejected: () => setInsertionRejected(true),
          }),
      },
      editable: isEditable,
    },
    [extensions]
  );

  useEffect(() => {
    onEditorChange(sectionId, editor ?? null);
  }, [editor, sectionId, onEditorChange]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(isEditable);
  }, [editor, isEditable]);

  // Debounced size measurement. The guard only reports: it never rejects an
  // update Yjs already applied, because that would drop work peers can see.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const measure = () => {
      // Encoded once: at the hard limit this is the heaviest state the section
      // will ever hold, so classifying and reporting must share the measurement.
      const encodedBytes = measureSectionBytes(sectionDocument);
      const level = classifySectionSize(encodedBytes);
      setSizeLevel(level);

      if (level !== "error") {
        reportedOversize.current = false;
        return;
      }
      if (reportedOversize.current) return;
      reportedOversize.current = true;
      reportOversizedSection({ documentId, sectionId, encodedBytes });
    };

    const scheduleMeasure = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(measure, SIZE_MEASURE_DEBOUNCE_MS);
    };

    scheduleMeasure();
    sectionDocument.on("update", scheduleMeasure);

    return () => {
      if (timer) clearTimeout(timer);
      sectionDocument.off("update", scheduleMeasure);
    };
  }, [sectionDocument, documentId, sectionId, measureSectionBytes]);

  return (
    // A labelled <section> is an accessible region, which is what a section
    // boundary is: the editor inside it owns keyboard focus.
    // biome-ignore lint/a11y/useKeyWithClickEvents: the nested editor owns keyboard focus
    <section
      aria-label={boundaryLabel}
      onClick={() => onActivate(sectionId)}
      onFocus={() => onActivate(sectionId)}
      className={cn(
        "rounded-lg border bg-card",
        isActive ? "border-primary" : "border-border",
        sizeLevel === "error" && "border-destructive"
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-medium">{boundaryLabel}</h2>
          <output className="text-xs text-muted-foreground">
            {hasSynced ? `${boundaryLabel} 동기화됨` : `${boundaryLabel} 동기화 중...`}
          </output>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            {sizeLevel !== "ok" && (
              <Button
                variant="secondary"
                size="sm"
                aria-label={`섹션 ${position} 분할`}
                onClick={() => onSplit(sectionId)}
                className="h-8 text-xs"
              >
                섹션 분할
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label={`섹션 ${position} 삭제`}
              disabled={isLastRemainingSection}
              title={
                isLastRemainingSection
                  ? "마지막 섹션은 삭제할 수 없습니다"
                  : `섹션 ${position} 삭제`
              }
              onClick={() => onDelete(sectionId)}
              className="h-8 text-xs"
            >
              삭제
            </Button>
          </div>
        )}
      </header>

      {sizeLevel === "warning" && (
        <output className="block px-4 py-2 text-xs text-muted-foreground">
          섹션 분할 권장: 이 섹션이 768 KiB를 넘었습니다. 커서를 옮긴 뒤 섹션 분할을 눌러 이후
          블록을 새 섹션으로 옮기세요.
        </output>
      )}

      {sizeLevel === "error" && (
        <p role="alert" className="px-4 py-2 text-xs text-destructive">
          섹션 크기가 1 MiB를 넘었습니다. 편집 내용은 유지되지만 즉시 분할이 필요합니다.
        </p>
      )}

      {isUnavailable && (
        <p role="alert" className="px-4 py-2 text-xs text-destructive">
          {boundaryLabel}에 접근할 수 없어 이 섹션의 편집을 중지했습니다. 다른 섹션은 그대로 편집할
          수 있습니다.
        </p>
      )}

      {insertionRejected && (
        <p role="alert" className="px-4 py-2 text-xs text-destructive">
          붙여넣으려는 블록 하나가 768 KiB를 넘어 의미를 보존한 분할이 불가능합니다. 파일 첨부를
          사용하거나 여러 섹션으로 나눠 입력하세요.
        </p>
      )}

      {/* Composition is tracked on the wrapper so a room that loses access
          mid-composition keeps `contenteditable` until the syllable is done. */}
      <div onCompositionStart={onCompositionStart} onCompositionEnd={onCompositionEnd}>
        <EditorContent editor={editor} />
      </div>
    </section>
  );
}

interface PasteGuardInput {
  view: EditorView;
  event: ClipboardEvent;
  slice: Slice;
  onRejected: () => void;
}

/**
 * Pre-checks a paste before it becomes one oversized top-level block.
 *
 * Oversized plain text is re-flowed into paragraphs, while a block that cannot be
 * split without losing meaning is refused so the section never grows past the
 * point a rebalance can repair.
 */
function guardPastedSlice({ view, event, slice, onRejected }: PasteGuardInput): boolean {
  const largest = largestTopLevelBlock(slice);
  if (!largest) return false;

  const decision = evaluateInsertionGuard({
    encodedBytes: measureBlockEncodedBytes(view.state.schema, largest),
    isAtomicBlock: !SPLITTABLE_BLOCK_TYPES.has(largest.type.name),
    plainText: event.clipboardData?.getData("text/plain") ?? "",
  });

  if (decision.kind === "allow") return false;

  if (decision.kind === "reject") {
    onRejected();
    return true;
  }

  const schema = view.state.schema;
  const paragraphs = decision.paragraphs.map((text) =>
    schema.nodes.paragraph.create(null, schema.text(text))
  );
  view.dispatch(
    view.state.tr.replaceSelection(new Slice(Fragment.fromArray(paragraphs), 0, 0)).scrollIntoView()
  );
  return true;
}

function largestTopLevelBlock(slice: Slice): ProseMirrorNode | null {
  let largest: ProseMirrorNode | null = null;
  slice.content.forEach((node) => {
    if (!largest || node.nodeSize > largest.nodeSize) largest = node;
  });
  return largest;
}

/** Encoded Yjs size the block would add, measured on a throwaway document. */
function measureBlockEncodedBytes(schema: Schema, block: ProseMirrorNode): number {
  const probe = new Y.Doc();
  const fragment = probe.getXmlFragment("default");
  prosemirrorJSONToYXmlFragment(schema, { type: "doc", content: [block.toJSON()] }, fragment);
  return Y.encodeStateAsUpdate(probe).byteLength;
}
